import type { Category } from '@shopgnd/shared';

export type CategoryTreeNode = Category & { children: CategoryTreeNode[] };

/** Sort key for categories at the same tree level. */
export function compareCategoryOrder(a: Category, b: Category): number {
  const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return a.name.localeCompare(b.name);
}

/** Categories sharing the same parent (undefined parentId = top-level). */
export function getSiblingCategories(categories: Category[], parentId?: string): Category[] {
  const parentKey = parentId || undefined;
  return categories
    .filter((c) => (c.parentId ?? undefined) === parentKey)
    .sort(compareCategoryOrder);
}

export function nextSortOrderForParent(categories: Category[], parentId?: string): number {
  const siblings = getSiblingCategories(categories, parentId);
  const max = siblings.reduce((m, c) => Math.max(m, c.sortOrder ?? 0), 0);
  return max + 1;
}

export function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const nodes = new Map<string, CategoryTreeNode>(
    categories.map((c) => [c.id, { ...c, children: [] }]),
  );
  const roots: CategoryTreeNode[] = [];

  for (const cat of categories) {
    const node = nodes.get(cat.id);
    if (!node) continue;
    if (cat.parentId && nodes.has(cat.parentId)) {
      nodes.get(cat.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (list: CategoryTreeNode[]) => {
    list.sort(compareCategoryOrder);
    list.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

/** Category id plus all nested subcategory ids. */
export function collectDescendantIds(categories: Category[], categoryId: string): string[] {
  const ids = new Set<string>([categoryId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of categories) {
      if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
        ids.add(c.id);
        changed = true;
      }
    }
  }
  return [...ids];
}

export function flattenCategoryTree(
  nodes: CategoryTreeNode[],
  depth = 0,
): Array<{ category: Category; depth: number }> {
  const out: Array<{ category: Category; depth: number }> = [];
  for (const node of nodes) {
    out.push({ category: node, depth });
    out.push(...flattenCategoryTree(node.children, depth + 1));
  }
  return out;
}

export function getCategoryPath(categories: Category[], categoryId: string): Category[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const path: Category[] = [];
  let current = byId.get(categoryId);
  while (current) {
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return path;
}

/** Prevent assigning a category as its own parent or under one of its descendants. */
export function listValidParentCategories(categories: Category[], excludeId?: string): Category[] {
  const excluded = excludeId ? new Set(collectDescendantIds(categories, excludeId)) : new Set<string>();
  return categories.filter((c) => !excluded.has(c.id));
}
